// NosotrosStreamingSection.js — Página «Nosotros» con el lenguaje del catálogo
// streaming (Learning): fondo oscuro, acento rojo, letra condensada.
//
//  · Portada: titular grande y, detrás, un muro de portadas reales de los cursos
//    que se desplaza despacio (el único movimiento que no pide el usuario).
//  · Cifras: se calculan del catálogo (cursos, lecciones, áreas) salvo que se escriban.
//  · Cómo aprendes (pasos), Lo que encuentras (pilares), Aval y llamada final.
// Todo el texto se edita en la sección (tipo `nosotros_streaming`).
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import MuroCursos from './MuroCursos';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

export const NOSOTROS_DEF = {
  eyebrow: 'Sobre Ateneo Digital',
  titulo: 'Formación en salud que se ve como una serie y se certifica como un curso universitario',
  texto: 'Ateneo Digital es la plataforma de cursos para profesionales de la salud: aprende y certifícate en los cursos de tu profesión y cumple con los cursos regulatorios y normativos, a tu ritmo y desde cualquier pantalla.',
  cifras_titulo: 'Lo que hay hoy en la plataforma',
  pasos_titulo: 'Cómo aprendes',
  pasos: [
    { titulo: 'Elige tu curso', texto: 'Explora el catálogo por área —gineco, farmacia, patología— y mira el avance de cada curso antes de entrar.' },
    { titulo: 'Aprende a tu ritmo', texto: 'Lecciones en video, contenidos interactivos, lecturas y documentos. La plataforma recuerda dónde quedaste.' },
    { titulo: 'Demuestra lo aprendido', texto: 'Exámenes al final de cada módulo o del curso, con la nota mínima clara desde el principio.' },
    { titulo: 'Certifícate', texto: 'Al completar el curso y aprobar sus exámenes obtienes tu certificado de finalización.' },
  ],
  pilares_titulo: 'Lo que encuentras',
  pilares: [
    { icono: 'video', titulo: 'Clases en video', texto: 'Docentes especialistas explican cada tema en sesiones cortas.' },
    { icono: 'interactivo', titulo: 'Contenido interactivo', texto: 'Módulos para recorrer, practicar y volver cuando lo necesites.' },
    { icono: 'progreso', titulo: 'Tu progreso siempre a la vista', texto: 'Cuántas lecciones llevas, qué te falta y dónde continuar.' },
    { icono: 'tutor', titulo: 'Un tutor que responde', texto: 'Pregunta sobre lo que estás viendo sin salir de la lección.' },
  ],
  aval_titulo: 'Avalados por la Universidad El Bosque',
  aval_texto: 'Todos los cursos cuentan con el aval académico de la Universidad El Bosque.',
  cta_titulo: '',        // vacío: sin llamada final (el footer ya trae la franja «Ver cursos»)
  cta_boton: 'Ver cursos',
  cta_url: '/',
};

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const ICONOS_NOSOTROS = {
  video: <svg viewBox="0 0 24 24" {...P}><rect x="3" y="5" width="18" height="13" rx="2.5" /><path d="m10.5 9 4 2.5-4 2.5Z" /></svg>,
  interactivo: <svg viewBox="0 0 24 24" {...P}><path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11" /><path d="M12 10.5V9a1.5 1.5 0 0 1 3 0v2" /><path d="M15 11a1.5 1.5 0 0 1 3 0v3.5a6 6 0 0 1-6 6h-.6a5.5 5.5 0 0 1-4.4-2.2L5 15.4a1.5 1.5 0 0 1 2.3-1.9L9 15" /></svg>,
  progreso: <svg viewBox="0 0 24 24" {...P}><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7.5 14.5 3.5-4 3 3 5-6" /></svg>,
  tutor: <svg viewBox="0 0 24 24" {...P}><path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.1A8 8 0 1 1 20 12Z" /><path d="M9 11h6M9 14h4" /></svg>,
  examen: <svg viewBox="0 0 24 24" {...P}><path d="M9 4h6v2H9z" /><path d="M8 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H16" /><path d="m9 13.5 2 2 4-4.5" /></svg>,
  certificado: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="9" r="5" /><path d="m9.5 13.3-1.5 7.2 4-2.2 4 2.2-1.5-7.2" /></svg>,
  celular: <svg viewBox="0 0 24 24" {...P}><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M11 18h2" /></svg>,
};

// ---------- estilos ----------
const Raiz = styled.div`
  --acento: ${p => p.$acento};
  background: ${p => p.$fondo}; color: #e7eaee; font-family: ${FUENTE};
  a { color: inherit; }
`;
const Portada = styled.section`
  position: relative; overflow: hidden; isolation: isolate;
  min-height: clamp(560px, 88vh, 820px); display: flex; align-items: flex-end;
  padding: 8rem 4vw clamp(3rem, 7vw, 5rem);
`;
const Titular = styled.div`
  max-width: 60rem;
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; margin-bottom: 1.2rem; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: 0; font-size: clamp(2.4rem, 6vw, 5.2rem); font-weight: 800; font-stretch: 78%; line-height: .98; letter-spacing: -.015em; color: #fff; text-wrap: balance; }
  p { margin: 1.4rem 0 0; max-width: 44rem; font-size: clamp(1rem, 1.35vw, 1.2rem); line-height: 1.6; color: #b9c0ca; }
`;
const Bloque = styled.section`
  padding: clamp(3.5rem, 7vw, 6rem) 4vw 0;
  h2 { margin: 0 0 2rem; font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 800; font-stretch: 82%; color: #fff; letter-spacing: -.01em; }
`;
const Cifras = styled.div`
  display: grid; grid-template-columns: repeat(${p => p.$n}, minmax(0, 1fr)); border-top: 1px solid rgba(255,255,255,.1);
  @media (max-width: 800px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  div { padding: 1.6rem 1.4rem 1.6rem 0; border-bottom: 1px solid rgba(255,255,255,.1); }
  b { display: block; font-size: clamp(3rem, 7vw, 5.5rem); font-weight: 800; font-stretch: 75%; line-height: .9; color: #fff; font-variant-numeric: tabular-nums; }
  span { display: block; margin-top: .6rem; font-size: .95rem; color: #9aa1ab; }
  div:first-child b { color: var(--acento); }
`;
const Pasos = styled.ol`
  list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(${p => p.$n}, minmax(0, 1fr)); gap: 1rem;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
  li { position: relative; padding: 1.4rem 1.3rem 1.5rem; border-radius: 16px; background: rgba(255,255,255,.04); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); }
  li::after { content: ''; position: absolute; left: 1.3rem; right: 1.3rem; top: 0; height: 2px; background: linear-gradient(90deg, var(--acento), transparent); }
  .n { display: block; font-size: 2.6rem; font-weight: 800; font-stretch: 75%; line-height: 1; color: var(--acento); margin-bottom: .8rem; }
  h3 { margin: 0 0 .5rem; font-size: 1.1rem; font-weight: 700; color: #fff; }
  p { margin: 0; font-size: .92rem; line-height: 1.55; color: #9aa1ab; }
`;
const Pilares = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 2rem 2.5rem;
  div { display: grid; grid-template-columns: auto 1fr; gap: .2rem 1rem; align-items: start; }
  i { grid-row: span 2; display: grid; place-items: center; width: 48px; height: 48px; border-radius: 14px;
    background: color-mix(in srgb, var(--acento) 16%, transparent); color: var(--acento); }
  svg { width: 24px; height: 24px; }
  h3 { margin: .15rem 0 0; font-size: 1.05rem; font-weight: 700; color: #fff; }
  p { margin: 0; font-size: .92rem; line-height: 1.55; color: #9aa1ab; }
`;
const Aval = styled.div`
  display: flex; align-items: center; gap: 1.5rem 2rem; flex-wrap: wrap;
  padding: clamp(1.6rem, 3vw, 2.4rem); border-radius: 20px;
  background: linear-gradient(120deg, color-mix(in srgb, var(--acento) 18%, transparent), rgba(255,255,255,.03) 60%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
  .sello { display: grid; place-items: center; flex: none; width: 76px; height: 76px; border-radius: 50%;
    background: var(--acento); color: #fff; box-shadow: 0 12px 30px -12px var(--acento); }
  .sello svg { width: 36px; height: 36px; }
  img { height: 64px; width: auto; }
  h3 { margin: 0; font-size: clamp(1.3rem, 2.4vw, 1.8rem); font-weight: 800; font-stretch: 82%; color: #fff; }
  p { margin: .35rem 0 0; color: #b9c0ca; }
`;
const Llamada = styled.section`
  padding: clamp(4rem, 8vw, 7rem) 4vw; text-align: center;
  h2 { margin: 0 auto 1.6rem; max-width: 22ch; font-size: clamp(2rem, 4.5vw, 3.6rem); font-weight: 800; font-stretch: 78%; line-height: 1; color: #fff; text-wrap: balance; }
  a { display: inline-flex; align-items: center; gap: .55rem; height: 52px; padding: 0 1.6rem; border-radius: 999px; text-decoration: none;
    background: var(--acento); color: #fff; font-size: 1.05rem; font-weight: 700; box-shadow: 0 14px 34px -14px var(--acento);
    transition: transform .15s ease, box-shadow .2s; }
  a:hover { transform: translateY(-2px); box-shadow: 0 18px 38px -14px var(--acento); }
  a:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
  svg { width: 18px; height: 18px; }
`;

const NosotrosStreamingSection = ({ config = {}, proyecto }) => {
  const c = { ...NOSOTROS_DEF, ...Object.fromEntries(Object.entries(config).filter(([, v]) => v !== '' && v != null)) };
  const { user } = useAuth();
  const secciones = proyecto?.config_json?.landing_secciones || [];
  const cat = secciones.find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = c.color_acento || cat.color_acento || '#E50914';
  const fondo = c.color_fondo || cat.color_fondo || '#141414';
  const docs = proyecto?.documentos || [];

  // Cifras reales del catálogo (o las escritas en la sección).
  const cifras = useMemo(() => {
    if (Array.isArray(config.cifras) && config.cifras.length) return config.cifras;
    const reales = (cat.items || []).filter(it => it.documento_id);
    const lecciones = reales.reduce((n, it) => {
      const d = docs.find(x => String(x.id) === String(it.documento_id));
      return n + (d?.modulos || []).reduce((k, m) => k + (m.lecciones?.length || 0), 0);
    }, 0);
    const areas = new Set(reales.map(it => (it.categoria || '').trim()).filter(Boolean)).size;
    return [
      { valor: String(reales.length), etiqueta: reales.length === 1 ? 'curso disponible' : 'cursos disponibles' },
      lecciones ? { valor: String(lecciones), etiqueta: 'lecciones para ver a tu ritmo' } : null,
      areas ? { valor: String(areas), etiqueta: areas === 1 ? 'área de formación' : 'áreas de formación' } : null,
      { valor: '100 %', etiqueta: 'en línea, desde cualquier pantalla' },
    ].filter(Boolean);
  }, [config.cifras, cat.items, docs]);

  const pasos = (Array.isArray(c.pasos) ? c.pasos : []).filter(p => p.titulo);
  const pilares = (Array.isArray(c.pilares) ? c.pilares : []).filter(p => p.titulo);
  const ctaUrl = user && c.cta_url === '/' ? '/contenidos' : c.cta_url;

  return (
    <Raiz $acento={acento}>
      <Portada>
        <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} imagen={c.imagen_banner} />
        <Titular>
          {c.eyebrow && <span className="eyebrow">{c.eyebrow}</span>}
          <h1>{c.titulo}</h1>
          {c.texto && <p>{c.texto}</p>}
        </Titular>
      </Portada>

      {c.mostrar_cifras !== false && cifras.length > 0 && (
        <Bloque aria-label={c.cifras_titulo}>
          {c.cifras_titulo && <h2>{c.cifras_titulo}</h2>}
          <Cifras $n={Math.min(cifras.length, 4)}>
            {cifras.map((x, i) => <div key={i}><b>{x.valor}</b><span>{x.etiqueta}</span></div>)}
          </Cifras>
        </Bloque>
      )}

      {pasos.length > 0 && (
        <Bloque>
          <h2>{c.pasos_titulo}</h2>
          <Pasos $n={Math.min(pasos.length, 4)}>
            {pasos.map((p, i) => <li key={i}><span className="n" aria-hidden="true">{i + 1}</span><h3>{p.titulo}</h3><p>{p.texto}</p></li>)}
          </Pasos>
        </Bloque>
      )}

      {pilares.length > 0 && (
        <Bloque>
          <h2>{c.pilares_titulo}</h2>
          <Pilares>
            {pilares.map((p, i) => (
              <div key={i}><i aria-hidden="true">{ICONOS_NOSOTROS[p.icono] || ICONOS_NOSOTROS.video}</i><h3>{p.titulo}</h3><p>{p.texto}</p></div>
            ))}
          </Pilares>
        </Bloque>
      )}

      {c.aval_titulo && (
        <Bloque>
          <Aval>
            {c.aval_logo
              ? <img src={urlImg(c.aval_logo)} alt="" />
              : <span className="sello" aria-hidden="true">{ICONOS_NOSOTROS.certificado}</span>}
            <div><h3>{c.aval_titulo}</h3>{c.aval_texto && <p>{c.aval_texto}</p>}</div>
          </Aval>
        </Bloque>
      )}

      {c.cta_titulo && (
        <Llamada>
          <h2>{c.cta_titulo}</h2>
          {c.cta_boton && (
            <Link to={ctaUrl || '/'}>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15a1 1 0 0 0 1.52.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>
              {user && c.cta_url === '/' ? 'Continuar aprendiendo' : c.cta_boton}
            </Link>
          )}
        </Llamada>
      )}
    </Raiz>
  );
};

export default NosotrosStreamingSection;
